import React, { useEffect, useState } from 'react';
import { MenuIcon, BellIcon, SearchIcon, ArrowLeftIcon, XIcon, ActivityIcon, AlertCircleIcon } from 'lucide-react';
import { useClient } from '../contexts/ClientContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
export const Header = () => {
  const {
    isImpersonating,
    setIsImpersonating,
    setCurrentClient
  } = useClient();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showNotifications, setShowNotifications] = useState(false);
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  const handleExitImpersonation = () => {
    setIsImpersonating(false);
    setCurrentClient(null);
    navigate('/admin/clients');
  };
  const handleSignOut = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  const mockNotifications = [{
    id: 1,
    type: 'alert',
    message: 'New security patch available',
    time: '2 minutes ago',
    icon: AlertCircleIcon,
    color: 'text-red-400'
  }, {
    id: 2,
    type: 'activity',
    message: 'System performance report ready',
    time: '10 minutes ago',
    icon: ActivityIcon,
    color: 'text-blue-400'
  }];
  return <header className="bg-surface-light border-b border-surface-border sticky top-0 z-40" data-id="element-562">
      <div className="max-w-7xl mx-auto" data-id="element-563">
        <div className="px-2 sm:px-6 lg:px-8" data-id="element-564">
          <div className="flex items-center justify-between h-14 sm:h-16" data-id="element-565">
            {/* Left section */}
            <div className="flex items-center gap-2 sm:gap-4" data-id="element-566">
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-1.5 sm:p-2 rounded-none hover:bg-surface-mid active:bg-surface-light transition-colors lg:hidden" aria-label="Toggle menu" aria-expanded={isMobileMenuOpen} data-id="element-567">
                {isMobileMenuOpen ? <XIcon size={18} className="text-gray-300" data-id="element-568" /> : <MenuIcon size={18} className="text-gray-300" data-id="element-569" />}
              </button>
              <div className="flex items-center gap-2 sm:gap-3" data-id="element-570">
                <div className="relative group" data-id="element-571">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/1Jh6cc5VaUNbQaWHvFaseP/Sprinklenet_Logo_Black_with_Transparent_Background.png" alt="Sprinklenet" className="h-5 sm:h-8 w-auto invert transition-transform duration-200 group-hover:scale-105" data-id="element-572" />
                  <div className="absolute -bottom-1 left-0 w-full h-0.5 bg-accent scale-x-0 group-hover:scale-x-100 transition-transform duration-200" data-id="element-573" />
                </div>
                <div className="hidden sm:block" data-id="element-574">
                  <span className="text-base sm:text-xl font-semibold text-white" data-id="element-575">
                    Knowledge Spaces
                  </span>
                  <div className="text-xs text-gray-400 font-mono" data-id="element-576">
                    {currentTime.toLocaleTimeString()} UTC
                  </div>
                </div>
              </div>
            </div>
            {/* Center section - Add Exit Workspace button when impersonating */}
            {isImpersonating && <button onClick={handleExitImpersonation} className="hidden md:flex items-center gap-2 px-4 py-2 bg-status-error/10 border border-status-error/20 
                         text-status-error hover:bg-status-error/20 transition-colors rounded-none" data-id="element-577">
                <ArrowLeftIcon size={16} data-id="element-578" />
                <span className="font-mono text-sm" data-id="element-579">Exit Client Workspace</span>
              </button>}
            {/* Right section */}
            <div className="flex items-center gap-1 sm:gap-4" data-id="element-580">
              {/* Mobile search trigger */}
              <button onClick={() => setIsSearchOpen(!isSearchOpen)} className="p-1.5 sm:p-2 rounded-none hover:bg-surface-mid transition-colors md:hidden" aria-label="Search" data-id="element-581">
                <SearchIcon size={18} className="text-gray-300" data-id="element-582" />
              </button>
              {/* Notifications - Improved mobile dropdown */}
              <div className="relative" data-id="element-583">
                <button onClick={() => {
                setShowNotifications(!showNotifications);
                if (isMobileMenuOpen) setIsMobileMenuOpen(false);
              }} className="relative p-1.5 sm:p-2 rounded-none hover:bg-surface-mid transition-colors group" aria-label="View notifications" data-id="element-584">
                  <BellIcon size={18} className="text-gray-300 group-hover:text-white transition-colors" data-id="element-585" />
                  <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-accent rounded-none animate-pulse" data-id="element-586" />
                </button>
                {showNotifications && <div className="fixed sm:absolute inset-x-0 top-[3.5rem] sm:top-full sm:right-0 sm:left-auto mt-0 
                             sm:mt-2 w-full sm:w-80 bg-surface-light border-b sm:border border-surface-border 
                             sm:rounded-none shadow-lg animate-in fade-in slide-in-from-top-2 duration-200 z-[60]" data-id="element-587">
                    <div className="p-3 sm:p-4 border-b border-surface-border flex items-center justify-between" data-id="element-588">
                      <h3 className="text-sm font-medium text-white font-mono" data-id="element-589">
                        Notifications
                      </h3>
                      <button onClick={() => setShowNotifications(false)} className="sm:hidden p-1 text-gray-400 hover:text-gray-300" data-id="element-590">
                        <XIcon size={16} data-id="element-591" />
                      </button>
                    </div>
                    <div className="divide-y divide-surface-border max-h-[60vh] sm:max-h-[400px] overflow-y-auto" data-id="element-592">
                      {mockNotifications.map(notification => <div key={notification.id} className="p-3 sm:p-4 hover:bg-surface-mid transition-colors" data-id="element-593">
                          <div className="flex items-start gap-3" data-id="element-594">
                            <div className="p-1.5 sm:p-2 bg-surface rounded-none" data-id="element-595">
                              <notification.icon size={16} className={notification.color} data-id="element-596" />
                            </div>
                            <div data-id="element-597">
                              <p className="text-sm text-white" data-id="element-598">
                                {notification.message}
                              </p>
                              <p className="text-xs text-gray-400 font-mono mt-1" data-id="element-599">
                                {notification.time}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>
                    <div className="p-2 sm:p-3 border-t border-surface-border" data-id="element-600">
                      <button className="w-full px-3 py-2 text-sm text-accent hover:text-accent-light transition-colors font-mono" data-id="element-601">
                        View All Notifications
                      </button>
                    </div>
                  </div>}
              </div>
              {/* User Menu - Improved mobile size */}
              <div className="relative" data-id="element-602">
                <button onClick={() => setIsMobileMenuOpen(false)} className="h-7 w-7 sm:h-9 sm:w-9 rounded-none overflow-hidden ring-1 ring-surface-border hover:ring-accent transition-all duration-200" aria-label="Open user menu" data-id="element-603">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/7chvFQtyuMRAzzqDdac1ka/Jamie_Thompson_of_Sprinklenet_White_Shirt_Dark_Background_2025.jpg" alt="Jamie Thompson" className="w-full h-full object-cover transition-transform duration-200 hover:scale-105" data-id="element-604" />
                </button>
              </div>
            </div>
          </div>
        </div>
        {/* Mobile Search Overlay - Improved focus and animation */}
        {isSearchOpen && <div className="fixed inset-0 bg-surface z-50 md:hidden animate-in fade-in duration-200" onClick={e => e.target === e.currentTarget && setIsSearchOpen(false)} data-id="element-605">
            <div className="p-4" data-id="element-606">
              <div className="flex items-center gap-3" data-id="element-607">
                <button onClick={() => setIsSearchOpen(false)} className="p-2 hover:bg-surface-mid active:bg-surface-light rounded-none transition-colors" data-id="element-608">
                  <ArrowLeftIcon size={18} className="text-gray-300" data-id="element-609" />
                </button>
                <div className="flex-1 relative" data-id="element-610">
                  <input type="text" placeholder="Search knowledge spaces..." className="w-full pl-9 pr-3 py-2 bg-surface-light border border-surface-border rounded-none 
                             text-sm text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-1 
                             focus:ring-accent focus:border-accent transition-all" autoFocus onFocus={e => e.target.select()} data-id="element-611" />
                  <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-612" />
                </div>
              </div>
            </div>
          </div>}
        {/* Mobile Menu - Improved animation and spacing */}
        {isMobileMenuOpen && <div className="lg:hidden border-t border-surface-border animate-in slide-in-from-top-2 duration-200" data-id="element-613">
            <div className="px-2 py-3 space-y-2" data-id="element-614">
              <a href="/dashboard" className="block px-4 py-3 rounded-none text-sm font-medium text-white hover:bg-surface-mid 
                 active:bg-surface-light transition-colors" data-id="element-615">
                Dashboard
              </a>
              <a href="/spaces" className="block px-4 py-3 rounded-none text-sm font-medium text-gray-300 hover:text-white 
                 hover:bg-surface-mid active:bg-surface-light transition-colors" data-id="element-616">
                Knowledge Spaces
              </a>
              <div className="py-3 my-2 border-t border-surface-border" data-id="element-617">
                <div className="px-4 py-2" data-id="element-618">
                  <p className="text-sm font-medium text-white" data-id="element-619">
                    Jamie Thompson
                  </p>
                  <p className="text-xs text-gray-400 font-mono" data-id="element-620">
                    System Admin
                  </p>
                </div>
                <a href="/settings" className="block px-4 py-3 rounded-none text-sm font-medium text-gray-300 hover:text-white 
                   hover:bg-surface-mid active:bg-surface-light transition-colors" data-id="element-621">
                  Settings
                </a>
                <button onClick={handleSignOut} className="w-full text-left px-4 py-3 rounded-none text-sm font-medium text-gray-300 
                   hover:text-white hover:bg-surface-mid active:bg-surface-light transition-colors" data-id="element-622">
                  Sign out
                </button>
              </div>
              {/* Single Exit Workspace button for mobile with improved touch target */}
              {isImpersonating && <div className="px-2 pt-2" data-id="element-623">
                  <button onClick={handleExitImpersonation} className="w-full flex items-center gap-2 px-4 py-3 bg-status-error/10 text-status-error 
                     hover:bg-status-error/20 active:bg-status-error/30 transition-colors rounded-none 
                     text-sm font-medium" data-id="element-624">
                    <ArrowLeftIcon size={16} data-id="element-625" />
                    <span data-id="element-626">Exit Client Workspace</span>
                  </button>
                </div>}
            </div>
          </div>}
      </div>
    </header>;
};